// ===== World 13: The hiccup robot's workshop – ELECTRICITY AND SWITCHES =====
// Power needs a whole way round: battery -> wire -> machine -> wire -> back to the battery.
// A gap in the wire = nothing happens. A switch closes the gap. Power makes machines work (gate, belt, lift, fan, robot).
// Hero: a small round steel robot ball -> goal: the robot's charging station (yellow box with a bolt).
// The circuit is solved every frame: conductors (cables, wires, closed switches) join points into "nodes";
// a machine has power if there is a path from one battery pole to one machine end and from the other end back.
import { defineWorld, definePart, defineObj } from '../../core/registry';
import { DEG, clamp, rr, lg, shadowOn, shadowOff, selBox } from '../../core/draw-helpers';
import type { Ctx2D, Gfx, GameBody, ObjData, PhysicsApi, SkinDef, SoundFn, WorldDef, WorldMeta } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { terms, type Pt, type TermObj } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
const RC = 30;          // how close a wire must be to a socket to connect
const WL = 160;         // length of the wire
const BL = 112;         // length of the battery

/** The level options the robot objects read (ObjData from the level) */
interface RobotObj extends ObjData {
  pts?: Pt[]; col?: string; quiet?: boolean; hold?: boolean; closed?: boolean;
  h?: number; w?: number; a?: number; dir?: number; v?: number; top?: number; len?: number; power?: number;
  pop?: number[]; every?: number; first?: number;
}
const opt = (o: ObjData) => o as RobotObj;

// ---------- geometry ----------
function dSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy;
  let u = L ? ((px - ax) * dx + (py - ay) * dy) / L : 0; u = clamp(u, 0, 1);
  return Math.hypot(px - ax - u * dx, py - ay - u * dy);
}
function cross(a: Pt, b: Pt, c: Pt) { return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]); }
function segSeg(a: Pt, b: Pt, c: Pt, d: Pt) {
  if (cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0) return 0;
  return Math.min(dSeg(a[0], a[1], c[0], c[1], d[0], d[1]), dSeg(b[0], b[1], c[0], c[1], d[0], d[1]), dSeg(c[0], c[1], a[0], a[1], b[0], b[1]), dSeg(d[0], d[1], a[0], a[1], b[0], b[1]));
}
function ends(pl: { x: number; y: number; a?: number }, L: number): Pt[] { const a = (pl.a || 0) * DEG, h = L / 2; return [[pl.x - Math.cos(a) * h, pl.y - Math.sin(a) * h], [pl.x + Math.cos(a) * h, pl.y + Math.sin(a) * h]]; }

// ---------- the circuit ----------
/** Common fields of circuit elements: plug points (terms), their indices (ti), and what the solver found */
interface ElBase { terms?: Pt[]; ti?: number[]; used?: boolean[]; fix?: boolean[]; live?: boolean }
/** Fixed cable from the level */
interface CabEl extends ElBase { k: 'cab'; terms: Pt[]; pts: Pt[] }
/** Wire placed by the player (a segment; anything near it connects) */
interface WireEl extends ElBase { k: 'wire'; seg: Pt[]; node?: number }
/** Fixed battery from the level */
interface BattEl extends ElBase { k: 'batt'; terms: Pt[]; x: number; y: number; shorted?: boolean; on?: boolean }
/** Battery placed by the player: its ends must sit on two plug points (src) */
interface PBattEl extends ElBase { k: 'pbatt'; ends: Pt[]; src?: [number, number] | null; shorted?: boolean; on?: boolean }
/** Knife switch */
interface SwEl extends ElBase { k: 'sw'; terms: Pt[]; closed: boolean }
/** Machine (lamp, gate, belt, lift, fan, robot) */
interface DevEl extends ElBase { k: 'dev'; terms: Pt[]; x: number; y: number; powered: boolean }
type El = CabEl | WireEl | BattEl | PBattEl | SwEl | DevEl;
type Src = BattEl | PBattEl;
interface Circuit { els: El[]; t: number }
interface Term { x: number; y: number; el: El; used: boolean; fix?: boolean }
declare module '../../core/types' {
  interface SimState { rb?: Circuit }
  // level data: a fixed cable is a polyline (pts), not placed by x/y; quiet = sockets don't blink (set from level 9 on)
  interface ObjDataTypes { robot_cable: { pts: Pt[]; quiet?: boolean; col?: string } }
}

function RB(api: PhysicsApi): Circuit { const S = api.S; if (!S.rb) S.rb = { els: [], t: -1 }; return S.rb; }
function solve(rb: Circuit, api: PhysicsApi) {
  const T: Term[] = [];
  for (const el of rb.els) if (el.terms) el.ti = el.terms.map(p => { T.push({ x: p[0], y: p[1], el, used: false }); return T.length - 1; });
  let N = T.length;
  for (const el of rb.els) if (el.k === 'wire') el.node = N++;
  const P = Array.from({ length: N }, (_, i) => i);
  const find = (P: number[], i: number) => { while (P[i] !== i) { P[i] = P[P[i]]; i = P[i]; } return i; };
  const uni = (P: number[], a: number, b: number) => { a = find(P, a); b = find(P, b); if (a !== b) P[a] = b; };
  for (let i = 0; i < T.length; i++) for (let j = i + 1; j < T.length; j++)
    if (T[i].el !== T[j].el && Math.hypot(T[i].x - T[j].x, T[i].y - T[j].y) < 12) { uni(P, i, j); T[i].used = T[j].used = T[i].fix = T[j].fix = true; }
  const wires = rb.els.filter((e): e is WireEl => e.k === 'wire');
  for (const el of rb.els) {
    if (el.k === 'cab' || (el.k === 'sw' && el.closed)) uni(P, el.ti![0], el.ti![1]);
    if (el.k === 'wire') {
      const [a, b] = el.seg;
      for (let i = 0; i < T.length; i++) if (dSeg(T[i].x, T[i].y, a[0], a[1], b[0], b[1]) < RC) { uni(P, el.node!, i); T[i].used = true; }
    }
  }
  for (let i = 0; i < wires.length; i++) for (let j = i + 1; j < wires.length; j++)
    if (segSeg(wires[i].seg[0], wires[i].seg[1], wires[j].seg[0], wires[j].seg[1]) < 16) uni(P, wires[i].node!, wires[j].node!);
  // batteries the child puts in
  const srcs: Src[] = [];
  for (const el of rb.els) {
    if (el.k === 'batt') srcs.push(el);
    if (el.k === 'pbatt') {
      el.src = null;
      const near = (p: Pt) => { let best = -1, bd = RC; for (let i = 0; i < T.length; i++) { const d = Math.hypot(T[i].x - p[0], T[i].y - p[1]); if (d < bd) { bd = d; best = i; } } return best; };
      const a = near(el.ends[0]), b = near(el.ends[1]);
      if (a >= 0 && b >= 0 && a !== b) { el.src = [a, b]; T[a].used = T[b].used = true; srcs.push(el); }
    }
  }
  const pole = (s: Src, k: number) => s.k === 'batt' ? s.ti![k] : s.src![k];
  const devs = rb.els.filter((e): e is DevEl => e.k === 'dev');
  for (const s of srcs) { s.shorted = find(P, pole(s, 0)) === find(P, pole(s, 1)); s.on = false; }
  for (const d of devs) {
    const was = d.powered; d.powered = false;
    if (find(P, d.ti![0]) !== find(P, d.ti![1])) for (const s of srcs) {
      if (s.shorted) continue;
      const Q = P.slice();
      for (const e of devs) if (e !== d) uni(Q, e.ti![0], e.ti![1]);
      for (const e of srcs) if (e !== s) uni(Q, pole(e, 0), pole(e, 1));
      const c = (i: number, j: number) => find(Q, i) === find(Q, j);
      const A = pole(s, 0), B = pole(s, 1);
      if ((c(A, d.ti![0]) && c(d.ti![1], B)) || (c(A, d.ti![1]) && c(d.ti![0], B))) { d.powered = true; s.on = true; break; }
    }
    if (d.powered && !was && api.S.started && rb.t >= 0) api.sfx('robot_hum', d.x, d.y, '#FFE36B', 10);
  }
  const live = new Set<number>();
  for (const d of devs) if (d.powered) { live.add(find(P, d.ti![0])); live.add(find(P, d.ti![1])); }
  for (const el of rb.els) {
    if (el.k === 'cab' || el.k === 'sw') el.live = live.has(find(P, el.ti![0])) && (el.k === 'cab' || el.closed);
    if (el.k === 'wire') el.live = live.has(find(P, el.node!));
    if (el.ti) { el.used = el.ti.map(i => T[i].used); el.fix = el.ti.map(i => !!T[i].fix); }
  }
}
function ensure(api: PhysicsApi) { const rb = RB(api); if (rb.t !== api.S.t) { solve(rb, api); rb.t = api.S.t; } return rb; }

// ---------- shared: contact in an area ----------
function bodiesIn(api: PhysicsApi, x0: number, x1: number, y0: number, y1: number) {
  const out: GameBody[] = [];
  for (const b of api.S.dyn) if (!b.isStatic && b.kind !== 'seesaw' && b.position.x > x0 && b.position.x < x1 && b.position.y > y0 && b.position.y < y1) out.push(b);
  return out;
}
function regDev(api: PhysicsApi, o: TermObj, extra?: Partial<DevEl>): DevEl { const el = Object.assign({ k: 'dev' as const, terms: terms(o), x: o.x, y: o.y, powered: false }, extra || {}); RB(api).els.push(el); return el; }

// ---------- drawing helpers ----------
function poly(ctx: Ctx2D, pts: Pt[], r?: number) {
  ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length - 1; i++) ctx.arcTo(pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1], r || 16);
  ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
}
function flow(ctx: Ctx2D, t: number) { ctx.setLineDash([5, 17]); ctx.lineDashOffset = -t * 1.6; ctx.strokeStyle = '#FFF8B8'; ctx.lineWidth = 5; ctx.stroke(); ctx.setLineDash([]); }
function cable(ctx: Ctx2D, pts: Pt[], live: boolean | undefined, t: number, col?: string) {
  ctx.save(); ctx.lineJoin = 'round'; ctx.lineCap = 'round'; poly(ctx, pts);
  if (live) { ctx.strokeStyle = 'rgba(255,224,70,.55)'; ctx.lineWidth = 22; ctx.stroke(); }
  ctx.strokeStyle = INK; ctx.lineWidth = 12; ctx.stroke();
  ctx.strokeStyle = live ? '#FF9A5C' : (col || '#E0564A'); ctx.lineWidth = 6.5; ctx.stroke();
  if (live) flow(ctx, t);
  ctx.restore();
}
function socket(ctx: Ctx2D, x: number, y: number, used: boolean | undefined, t: number, quiet?: boolean) {
  ctx.save();
  if (!used && !quiet) { const k = (Math.sin(t * 0.09) + 1) / 2; ctx.beginPath(); ctx.arc(x, y, 15 + k * 4, 0, 7); ctx.strokeStyle = `rgba(255,190,40,${0.35 + 0.45 * k})`; ctx.lineWidth = 3.5; ctx.stroke(); }
  ctx.beginPath(); ctx.arc(x, y, 10, 0, 7); ctx.fillStyle = '#FFD36B'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(x, y, 4.5, 0, 7); ctx.fillStyle = used ? '#E8A04A' : INK; ctx.fill();
  ctx.restore();
}
function screw(ctx: Ctx2D, x: number, y: number) { ctx.beginPath(); ctx.arc(x, y, 7, 0, 7); ctx.fillStyle = '#FFD36B'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke(); ctx.beginPath(); ctx.moveTo(x - 4, y); ctx.lineTo(x + 4, y); ctx.lineWidth = 2; ctx.stroke(); }
function bolt(ctx: Ctx2D, x: number, y: number, s: number, fill?: string) {
  ctx.save(); ctx.translate(x, y); ctx.scale(s, s);
  ctx.beginPath(); ctx.moveTo(3, -16); ctx.lineTo(-9, 2); ctx.lineTo(-1, 2); ctx.lineTo(-4, 16); ctx.lineTo(9, -3); ctx.lineTo(1, -3); ctx.closePath();
  ctx.fillStyle = fill || '#FFE36B'; ctx.fill(); ctx.lineWidth = 2.5 / s; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.stroke(); ctx.restore();
}
// differs from draw-helpers outline: solid INK (no alpha)
function box(ctx: Ctx2D, g: Gfx, x: number, y: number, w: number, h: number, c1: string, c2: string, r?: number) {
  g.rr(ctx, x - w / 2, y - h / 2, w, h, r == null ? 10 : r); ctx.fillStyle = g.lg(ctx, y - h / 2, y + h / 2, c1, c2); ctx.fill();
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
}
function gear(ctx: Ctx2D, x: number, y: number, r: number, a: number, col?: string) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.beginPath();
  for (let i = 0; i < 16; i++) { const rr = i % 2 ? r : r * 0.72, an = i / 16 * Math.PI * 2; ctx.lineTo(Math.cos(an) * rr, Math.sin(an) * rr); }
  ctx.closePath(); ctx.fillStyle = col || '#FFC93C'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, r * 0.28, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.stroke(); ctx.restore();
}
function motorBox(ctx: Ctx2D, g: Gfx, x: number, y: number, on: boolean, t: number) {
  box(ctx, g, x, y, 70, 34, on ? '#9EE4FF' : '#BFD3E6', on ? '#4FB3E8' : '#8FA4BD', 9);
  gear(ctx, x - 12, y, 11, on ? t * 0.15 : 0, on ? '#FFC93C' : '#D8D2C2');
  ctx.beginPath(); ctx.arc(x + 18, y, 5, 0, 7); ctx.fillStyle = on ? '#3FE07A' : '#6E6780'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.stroke();
}

// Wire (part): red cable with copper plugs at the ends; kind 'string' = the wool string decoy
function drawWire(ctx: Ctx2D, pl: { x: number; y: number; a?: number }, sel: boolean, kind?: 'string') {
  ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG); ctx.lineCap = 'round';
  if (kind === 'string') {
    // wool string: thick twisted green yarn with frayed tassel ends – no metal, so it does not conduct
    const path = () => { ctx.beginPath(); ctx.moveTo(-66, 0); ctx.bezierCurveTo(-30, 12, 10, -10, 66, 2); };
    path(); ctx.strokeStyle = INK; ctx.lineWidth = 15; ctx.stroke();
    path(); ctx.strokeStyle = '#7CCB55'; ctx.lineWidth = 10; ctx.stroke();
    // twist: slanted stripes along the yarn
    ctx.strokeStyle = '#4E9A37'; ctx.lineWidth = 2.5;
    for (let i = 0; i <= 26; i++) {
      const u = i / 26, x = -66 + u * 132, y = 3 * (1 - u) * (1 - u) * u * 12 + 3 * (1 - u) * u * u * -10 + u * u * u * 2;
      ctx.beginPath(); ctx.moveTo(x - 3, y + 4); ctx.lineTo(x + 3, y - 4); ctx.stroke();
    }
    path(); ctx.strokeStyle = 'rgba(255,255,255,.35)'; ctx.lineWidth = 2; ctx.setLineDash([2, 7]); ctx.stroke(); ctx.setLineDash([]);
    // frayed tassels at both ends
    for (const [ex, ey, dir] of [[-66, 0, -1], [66, 2, 1]]) {
      ctx.lineCap = 'round';
      for (let k = -2; k <= 2; k++) {
        ctx.beginPath(); ctx.moveTo(ex, ey + k * 2); ctx.quadraticCurveTo(ex + dir * 8, ey + k * 4, ex + dir * 15, ey + k * 6 + (k % 2) * 2);
        ctx.strokeStyle = INK; ctx.lineWidth = 5; ctx.stroke(); ctx.strokeStyle = '#9FE07A'; ctx.lineWidth = 2.5; ctx.stroke();
      }
    }
  } else {
    ctx.beginPath(); ctx.moveTo(-60, 0); ctx.quadraticCurveTo(0, 16, 60, 0);
    ctx.strokeStyle = INK; ctx.lineWidth = 12; ctx.stroke(); ctx.strokeStyle = '#E0564A'; ctx.lineWidth = 6.5; ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-50, -1); ctx.quadraticCurveTo(0, 12, 50, -1); ctx.stroke();
    for (const s of [-1, 1]) {
      ctx.save(); ctx.scale(s, 1);
      rr(ctx, 56, -9, 18, 18, 5); ctx.fillStyle = '#9AA6BC'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
      rr(ctx, 72, -4, 10, 8, 3); ctx.fillStyle = '#F0A050'; ctx.fill(); ctx.lineWidth = 2.5; ctx.stroke();
      ctx.restore();
    }
  }
  if (sel) selBox(ctx, -90, -20, 180, 40, 14, true);
  ctx.restore();
}
// Battery (part and fixed): green with a bolt and a silver pole
function drawBattery(ctx: Ctx2D, x: number, y: number, a: number, sc: number, sel: boolean, on?: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0); ctx.scale(sc, sc);
  if (on) { ctx.beginPath(); ctx.ellipse(0, 0, 70, 34, 0, 0, 7); ctx.fillStyle = 'rgba(255,230,90,.35)'; ctx.fill(); }
  shadowOn(ctx, 8, 4);
  rr(ctx, -50, -20, 96, 40, 12); ctx.fillStyle = lg(ctx, -20, 20, '#9BEA6E', '#3FA535'); ctx.fill(); shadowOff(ctx);
  rr(ctx, 22, -20, 24, 40, 0); ctx.fillStyle = lg(ctx, -20, 20, '#E9EEF5', '#9AA6BC'); ctx.fill();
  rr(ctx, -50, -20, 96, 40, 12); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.moveTo(22, -20); ctx.lineTo(22, 20); ctx.stroke();
  rr(ctx, 46, -8, 9, 16, 3); ctx.fillStyle = '#C9D3E0'; ctx.fill(); ctx.stroke();
  rr(ctx, -44, -16, 60, 7, 3); ctx.fillStyle = 'rgba(255,255,255,.45)'; ctx.fill();
  bolt(ctx, -12, 1, 0.95, on ? '#FFF27A' : '#FFE36B');
  ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(29, 0); ctx.lineTo(39, 0); ctx.moveTo(34, -5); ctx.lineTo(34, 5); ctx.stroke();
  if (sel) selBox(ctx, -62, -30, 124, 60, 16, true);
  ctx.restore();
}

// ---------- drawings of the machines ----------
function lamp(ctx: Ctx2D, g: Gfx, x: number, y: number, on: boolean, t: number) {
  ctx.save();
  if (on) {
    const rg = ctx.createRadialGradient(x, y - 10, 6, x, y - 10, 60); rg.addColorStop(0, 'rgba(255,240,140,.85)'); rg.addColorStop(1, 'rgba(255,240,140,0)');
    ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(x, y - 10, 60, 0, 7); ctx.fill();
    ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.lineCap = 'round';
    for (let i = 0; i < 7; i++) { const a = Math.PI + i / 6 * Math.PI, r0 = 30 + Math.sin(t * 0.2 + i) * 2; ctx.beginPath(); ctx.moveTo(x + Math.cos(a) * r0, y - 10 + Math.sin(a) * r0); ctx.lineTo(x + Math.cos(a) * (r0 + 10), y - 10 + Math.sin(a) * (r0 + 10)); ctx.stroke(); }
  }
  ctx.beginPath(); ctx.arc(x, y - 12, 19, 0, 7); ctx.fillStyle = on ? '#FFF27A' : 'rgba(235,242,250,.95)'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.strokeStyle = on ? '#FF9A3C' : '#9AA6BC'; ctx.lineWidth = 2.5; ctx.beginPath(); ctx.moveTo(x - 6, y + 4); ctx.lineTo(x - 6, y - 12); ctx.lineTo(x - 2, y - 17); ctx.lineTo(x + 2, y - 12); ctx.lineTo(x + 6, y - 17); ctx.lineTo(x + 6, y + 4); ctx.stroke();
  g.rr(ctx, x - 12, y + 4, 24, 14, 4); ctx.fillStyle = '#B4BFCF'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  g.rr(ctx, x - 30, y + 16, 60, 12, 5); ctx.fillStyle = '#8E9BB3'; ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.ellipse(x - 7, y - 20, 5, 7, 0.4, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fill();
  screw(ctx, x - 24, y + 22); screw(ctx, x + 24, y + 22);
  ctx.restore();
}
// Empty battery holder (the battery is placed on top of it)
function holder(ctx: Ctx2D, g: Gfx, x: number, y: number) {
  ctx.save();
  g.rr(ctx, x - 66, y - 26, 132, 52, 12); ctx.fillStyle = 'rgba(80,72,104,.85)'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  g.rr(ctx, x - 56, y - 20, 112, 40, 10); ctx.setLineDash([7, 6]); ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = 2.5; ctx.stroke(); ctx.setLineDash([]);
  ctx.strokeStyle = '#C9D3E0'; ctx.lineWidth = 3;
  for (const s of [-1, 1]) { ctx.beginPath(); for (let i = 0; i <= 8; i++) ctx.lineTo(x + s * (60 - i * 1.6), y + (i % 2 ? -8 : 8)); ctx.stroke(); }
  bolt(ctx, x, y, 0.8, 'rgba(255,227,107,.45)');
  ctx.restore();
}
// Knife switch: open = 0 (closed) .. 1 (open)
function knife(ctx: Ctx2D, g: Gfx, x: number, y: number, open: number, live?: boolean) {
  ctx.save();
  g.rr(ctx, x - 46, y - 8, 92, 12, 5); ctx.fillStyle = '#FF8A70'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  for (const s of [-1, 1]) { g.rr(ctx, x + s * 30 - 6, y - 20, 12, 14, 3); ctx.fillStyle = '#F0A050'; ctx.fill(); ctx.stroke(); }
  ctx.save(); ctx.translate(x - 30, y - 14); ctx.rotate(-open * 0.85);
  ctx.lineCap = 'round';
  if (live) { ctx.strokeStyle = 'rgba(255,224,70,.6)'; ctx.lineWidth = 16; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(62, 0); ctx.stroke(); }
  ctx.strokeStyle = INK; ctx.lineWidth = 10; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(62, 0); ctx.stroke();
  ctx.strokeStyle = '#FFD36B'; ctx.lineWidth = 5; ctx.stroke();
  ctx.beginPath(); ctx.arc(70, 0, 8, 0, 7); ctx.fillStyle = '#E0394A'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.restore();
  ctx.beginPath(); ctx.arc(x - 30, y - 14, 4, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.restore();
}
function robotBody(ctx: Ctx2D, g: Gfx, x: number, y: number, on: boolean, hop: number, t: number) {
  ctx.save(); ctx.translate(x, y - hop * 10);
  ctx.save(); ctx.scale(1 + hop * 0.06, 1 - hop * 0.04);
  // ears with plugs
  for (const s of [-1, 1]) { g.rr(ctx, s * 60 - (s < 0 ? 18 : 0), -86, 18, 22, 6); ctx.fillStyle = '#8B96AA'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke(); }
  g.shadowOn(ctx, 12, 6); g.rr(ctx, -60, -110, 120, 110, 22); ctx.fillStyle = g.lg(ctx, -110, 0, '#9EDCFA', '#4FA3D9'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3.5; ctx.strokeStyle = INK; ctx.stroke();
  g.rr(ctx, -42, -94, 84, 52, 12); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 3; ctx.stroke();
  ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.lineCap = 'round';
  if (on) {
    for (const s of [-1, 1]) { const bl = (t % 150) < 6; if (bl) { ctx.beginPath(); ctx.moveTo(s * 16 - 6, -74); ctx.lineTo(s * 16 + 6, -74); ctx.stroke(); } else { g.rr(ctx, s * 16 - 5, -84, 10, 18, 5); ctx.fill(); } }
    if (hop > 0) { ctx.beginPath(); ctx.ellipse(0, -55, 6, 7, 0, 0, 7); ctx.fill(); } else { ctx.beginPath(); ctx.arc(0, -60, 9, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  } else { for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * 16, -76, 6, 0.1 * Math.PI, 0.9 * Math.PI); ctx.stroke(); } ctx.beginPath(); ctx.moveTo(-6, -56); ctx.lineTo(6, -56); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,120,150,.45)'; for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * 30, -60, 6, 4, 0, 0, 7); ctx.fill(); }
  // buttons on the belly
  ['#37C16B', '#FFC93C', '#FF5A5F'].forEach((c, i) => { ctx.beginPath(); ctx.arc(-24 + i * 24, -22, 7, 0, 7); ctx.fillStyle = on && ((t >> 3) + i) % 3 === 0 ? '#fff' : c; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke(); });
  // head plate (the ball rests here)
  g.rr(ctx, -64, -122, 128, 14, 6); ctx.fillStyle = '#C9D3E0'; ctx.fill(); ctx.lineWidth = 3; ctx.stroke();
  for (const s of [-1, 1]) { g.rr(ctx, s * 54 - 6, -124, 12, 16, 5); ctx.fillStyle = '#8B96AA'; ctx.fill(); ctx.stroke(); }
  ctx.restore();
  if (!on) { ctx.fillStyle = 'rgba(59,42,92,.5)'; for (let i = 0; i < 3; i++) { const k = ((t * 0.01 + i / 3) % 1); ctx.beginPath(); ctx.arc(70 + k * 30, -120 - k * 50, 3 + k * 4, 0, 7); ctx.fill(); } }
  ctx.restore();
}
/** Rectangle of a core part (worldRects), a in radians */
interface RRect { x: number; y: number; w: number; h: number; a: number }
// Plank as a green meccano strip with holes
function meccano(ctx: Ctx2D, g: Gfx, r: RRect, sel?: boolean) {
  // block only checks outline for truthiness (and always draws it in #FFC93C)
  g.block(ctx, r.x, r.y, r.w, r.h, r.a, '#8BE0A8', '#2F9E63', 8, { outline: sel ? '#FFC93C' : null });
  ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
  for (let x = -r.w / 2 + 12; x <= r.w / 2 - 10; x += 18) { ctx.beginPath(); ctx.arc(x, 0, 3.2, 0, 7); ctx.fillStyle = 'rgba(30,70,50,.7)'; ctx.fill(); }
  ctx.restore();
}
// Pad as a red spring board
function spring(ctx: Ctx2D, g: Gfx, r: RRect, sel?: boolean) {
  ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
  ctx.strokeStyle = INK; ctx.lineWidth = 6; ctx.lineJoin = 'round';
  const path = () => { ctx.beginPath(); for (let i = 0; i <= 10; i++) ctx.lineTo(-r.w / 2 + 10 + i * (r.w - 20) / 10, r.h / 2 + 4 + (i % 2 ? 10 : 0)); };
  path(); ctx.stroke(); ctx.strokeStyle = '#C9D3E0'; ctx.lineWidth = 3; path(); ctx.stroke();
  g.rr(ctx, -r.w / 2 - 4, r.h / 2 + 14, r.w + 8, 8, 4); ctx.fillStyle = '#6E6780'; ctx.fill();
  g.rr(ctx, -r.w / 2, -r.h / 2, r.w, r.h, r.h / 2); ctx.fillStyle = g.lg(ctx, -r.h / 2, r.h / 2, '#FF9A8A', '#E0394A'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  if (sel) selBox(ctx, -r.w / 2 - 5, -r.h / 2 - 5, r.w + 10, r.h + 32, 10);
  ctx.restore();
}
// Stone as a heavy hex nut
function nut(ctx: Ctx2D, g: Gfx, x: number, y: number, a: number, sel?: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.shadowOn(ctx, 10, 6); ctx.beginPath(); for (let i = 0; i < 6; i++) ctx.lineTo(Math.cos(i * Math.PI / 3) * 27, Math.sin(i * Math.PI / 3) * 27); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -27, 27, '#E9EEF5', '#8E9BB3'); ctx.fill(); g.shadowOff(ctx); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, 10, 0, 7); ctx.fillStyle = '#5E5773'; ctx.fill(); ctx.stroke();
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 33, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}

// ---------- skins ----------
const skins: Record<string, SkinDef> = {
  robot_shelf(ctx, b, g) {
    g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#DCE4EF', '#8E9BB3', Math.min(9, b.dh! / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle); ctx.fillStyle = 'rgba(59,42,92,.55)';
    if (b.dw! > b.dh!) for (let x = -b.dw! / 2 + 14; x < b.dw! / 2 - 6; x += 60) { ctx.beginPath(); ctx.arc(x, 0, 2.6, 0, 7); ctx.fill(); }
    else for (let y = -b.dh! / 2 + 14; y < b.dh! / 2 - 6; y += 60) { ctx.beginPath(); ctx.arc(0, y, 2.6, 0, 7); ctx.fill(); }
    ctx.restore();
  },
  robot_wall(ctx, b, g) {
    const dw = b.dw!, dh = b.dh!;
    g.block(ctx, b.position.x, b.position.y, dw, dh, b.angle, '#9EDCFA', '#4D9BCB', Math.min(10, dw / 2, dh / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle); ctx.fillStyle = 'rgba(255,255,255,.75)';
    const v = dh > dw, n = Math.max(1, Math.floor((v ? dh : dw) / 50));
    for (let i = 0; i < n; i++) { const k = -0.5 + (i + 0.5) / n; ctx.beginPath(); ctx.arc(v ? 0 : k * dw, v ? k * dh : 0, 3, 0, 7); ctx.fill(); }
    ctx.restore();
  },
  robot_table: { c1: '#F2C79A', c2: '#C98C5A', r: 8 },
};

// The hero: a small round steel robot with LED eyes (helper balls: copper)
function ball(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!;
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05);
  if (kind === 'hero') { gr.addColorStop(0, '#FFFFFF'); gr.addColorStop(0.5, '#C9D3E0'); gr.addColorStop(1, '#7B88A3'); }
  else { gr.addColorStop(0, '#FFE6C4'); gr.addColorStop(0.5, '#EE9A50'); gr.addColorStop(1, '#A8561E'); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.stroke();
  ctx.save(); ctx.rotate(b.roll || 0);
  if (kind === 'hero') {
    ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(0, -r); ctx.lineTo(0, -r - 5); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -r - 6, 3, 0, 7); ctx.fillStyle = (t >> 4) % 2 ? '#FF5A5F' : '#FF9A9A'; ctx.fill();
    g.rr(ctx, -r * .74, -r * .46, r * 1.48, r * .64, r * .3); ctx.fillStyle = INK; ctx.fill();
    const blink = (t % 170) < 6;
    for (const s of [-1, 1]) {
      ctx.fillStyle = '#7FF3FF';
      if (b.sad) { ctx.strokeStyle = '#7FF3FF'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(s * r * .34, -r * .05, r * .14, 1.15 * Math.PI, 1.85 * Math.PI); ctx.stroke(); }
      else if (blink) ctx.fillRect(s * r * .34 - r * .16, -r * .16, r * .32, 2);
      else { ctx.beginPath(); ctx.ellipse(s * r * .34, -r * .14, r * .13, r * .18, 0, 0, 7); ctx.fill(); }
    }
    ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
    if (b.speed > 8.5) { ctx.beginPath(); ctx.ellipse(0, r * .48, r * .14, r * .17, 0, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
    else if (b.sad) { ctx.beginPath(); ctx.arc(0, r * .7, r * .22, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
    else { ctx.beginPath(); ctx.arc(0, r * .3, r * .26, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
    ctx.fillStyle = 'rgba(255,120,150,.5)'; for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * r * .62, r * .32, r * .13, r * .08, 0, 0, 7); ctx.fill(); }
  } else {
    ctx.strokeStyle = 'rgba(120,50,10,.5)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(0, 0, r * .55, 0, 7); ctx.stroke();
    ctx.fillStyle = INK; for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * r * .3, -r * .1, r * .12, 0, 7); ctx.fill(); }
    ctx.beginPath(); ctx.arc(0, r * .15, r * .22, 0.15 * Math.PI, 0.85 * Math.PI); ctx.lineWidth = 1.6; ctx.strokeStyle = INK; ctx.stroke();
  }
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(-r * .4, -r * .48, r * .26, r * .15, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fill();
  ctx.restore();
}

// The goal: the robot's charging station (yellow box with a bolt)
interface GoalBox { x: number; y: number; w: number }
const goal = {
  bowl(ctx: Ctx2D, gl: GoalBox, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save(); g.shadowOn(ctx, 14, 7);
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 8, y - 60); ctx.lineTo(x + w / 2 + 8, y - 60); ctx.lineTo(x + w / 2 - 2, y + 10); ctx.lineTo(x - w / 2 + 2, y + 10); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 60, y + 10, '#FFE07A', '#F2A516'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3.5; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.stroke();
    ctx.beginPath(); ctx.ellipse(x, y - 60, w / 2 + 6, 9, 0, 0, 7); ctx.fillStyle = '#5E5773'; ctx.fill(); ctx.stroke();
    const k = full ? 0.6 + 0.4 * Math.sin(t * 0.3) : 0;
    if (full) { ctx.beginPath(); ctx.arc(x, y - 24, 30, 0, 7); ctx.fillStyle = `rgba(255,255,200,${0.5 * k})`; ctx.fill(); }
    bolt(ctx, x, y - 24, 1.35, full ? '#FFFFFF' : '#FFF6C0');
    for (let i = 0; i < 3; i++) { g.rr(ctx, x + w / 2 - 34 + i * 9, y - 10, 6, 12, 2); ctx.fillStyle = full && (t >> 3) % 4 > i ? '#3FE07A' : '#B9771A'; ctx.fill(); }
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: GoalBox) {
    const { x, y, w } = gl;
    ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 60, w / 2 + 6, 9, 0, 0, Math.PI); ctx.lineWidth = 6; ctx.strokeStyle = '#FFE07A'; ctx.stroke();
    ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.beginPath(); ctx.ellipse(x, y - 60, w / 2 + 9, 12, 0, 0.1, Math.PI - 0.1); ctx.stroke(); ctx.restore();
  },
};

// ---------- parts ----------
const parts = {
  robot_wire: definePart({
    rects: [{ x: 0, y: 0, w: WL, h: 16 }], rot: 15, defaultA: 0,
    draw(ctx, pl, sel) { drawWire(ctx, pl, sel); },
    icon(ctx) { drawWire(ctx, { x: 0, y: 0, a: -25 }, false); },
    create(api, pl) {
      const el: WireEl = { k: 'wire', seg: ends(pl, WL) }; RB(api).els.push(el);
      return { layer: 'back', draw(ctx) { drawWire(ctx, pl, false); } };
    },
    intro(ctx, t, g, h) {
      const k = h.ease(Math.min(1, Math.max(0, (t - 40) / 70))), on = t > 110;
      cable(ctx, [[150, 200], [80, 200], [80, 110], [150, 110]], on, t); cable(ctx, [[310, 110], [380, 110], [380, 200], [300, 200]], on, t);
      drawBattery(ctx, 115, 110, 0, 0.55, false, on);
      lamp(ctx, g, 345, 92, on, t); socket(ctx, 150, 200, on, t); socket(ctx, 300, 200, on, t);
      drawWire(ctx, { x: 225, y: 200 - (1 - k) * 120, a: 0 }, false);
      if (on) { ctx.save(); ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(150, 200); ctx.quadraticCurveTo(225, 214, 300, 200); flow(ctx, t); ctx.restore(); }
      if (!on) h.arrow(225, 40 + k * 60, 0, 30, false);
    },
  }),
  // Decoy: the wool string looks like a wire but does not conduct (no circuit element)
  robot_string: definePart({
    rects: [{ x: 0, y: 0, w: WL, h: 16 }], rot: 15, defaultA: 0,
    draw(ctx, pl, sel) { drawWire(ctx, pl, sel, 'string'); },
    icon(ctx) { drawWire(ctx, { x: 0, y: 0, a: -25 }, false, 'string'); },
    create(api, pl) { return { layer: 'back', draw(ctx) { drawWire(ctx, pl, false, 'string'); } }; },
    // Video: wool string in the gap = the lamp stays off (wool does not conduct). Wire in the gap = the lamp lights.
    intro(ctx, t, g, h) {
      const ks = h.ease((t - 20) / 40), up = h.ease((t - 105) / 25), kw = h.ease((t - 135) / 35), on = t > 172;
      cable(ctx, [[150, 200], [80, 200], [80, 110], [150, 110]], on, t); cable(ctx, [[310, 110], [380, 110], [380, 200], [300, 200]], on, t);
      drawBattery(ctx, 115, 110, 0, 0.55, false, on);
      lamp(ctx, g, 345, 92, on, t); socket(ctx, 150, 200, t > 60 && t < 120 || on, t); socket(ctx, 300, 200, t > 60 && t < 120 || on, t);
      if (t < 132) drawWire(ctx, { x: 225, y: 200 - (1 - ks) * 130 - up * 150, a: 0 }, false, 'string');
      if (t > 128) drawWire(ctx, { x: 225, y: 200 - (1 - kw) * 130, a: 0 }, false);
      // red cross: nothing happens with the wool string
      if (t > 66 && t < 112) {
        ctx.save(); ctx.translate(225, 150); const pop = Math.min(1, (t - 66) / 8); ctx.scale(pop, pop);
        ctx.beginPath(); ctx.arc(0, 0, 26, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 4; ctx.strokeStyle = INK; ctx.stroke();
        ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(-11, -11); ctx.lineTo(11, 11); ctx.moveTo(11, -11); ctx.lineTo(-11, 11);
        ctx.strokeStyle = '#FF5A5F'; ctx.lineWidth = 7; ctx.stroke(); ctx.restore();
      }
      if (on) {
        ctx.save(); ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(150, 200); ctx.quadraticCurveTo(225, 214, 300, 200); flow(ctx, t); ctx.restore();
        ctx.save(); ctx.translate(225, 150); const pop = Math.min(1, (t - 172) / 8); ctx.scale(pop, pop);
        ctx.beginPath(); ctx.arc(0, 0, 26, 0, 7); ctx.fillStyle = '#3FC46A'; ctx.fill(); ctx.lineWidth = 4; ctx.strokeStyle = INK; ctx.stroke();
        ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath(); ctx.moveTo(-11, 1); ctx.lineTo(-3, 9); ctx.lineTo(12, -8);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 6; ctx.stroke(); ctx.restore();
      }
    },
  }),
  robot_battery: definePart({
    rects: [{ x: 0, y: 0, w: BL, h: 40 }], rot: 15, defaultA: 0,
    draw(ctx, pl, sel) { drawBattery(ctx, pl.x, pl.y, (pl.a || 0) * DEG, 1, sel); },
    icon(ctx) { drawBattery(ctx, 0, 0, -0.4, 0.85, false); },
    create(api, pl) {
      const el: PBattEl = { k: 'pbatt', ends: ends(pl, BL) }; RB(api).els.push(el);
      return { layer: 'back', draw(ctx) { drawBattery(ctx, pl.x, pl.y, (pl.a || 0) * DEG, 1, false, el.on); } };
    },
    intro(ctx, t, g, h) {
      const k = h.ease(Math.min(1, Math.max(0, (t - 40) / 70))), on = t > 110;
      cable(ctx, [[170, 200], [90, 200], [90, 110], [320, 110]], on, t); cable(ctx, [[370, 110], [390, 110], [390, 200], [290, 200]], on, t);
      holder(ctx, g, 230, 200);
      lamp(ctx, g, 345, 92, on, t);
      drawBattery(ctx, 230, 200 - (1 - k) * 130, 0, 1, false, on);
      if (!on) h.arrow(230, 30 + k * 40, 0, 30, false);
    },
  }),
};

// ---------- objects ----------
interface CableSt { el: CabEl }
interface BattSt { el: BattEl }
interface DevSt { el: DevEl }
interface SwitchSt { el: SwEl; down: number }
interface GateSt { b: GameBody; el: DevEl; off: number; h: number; hy: number }
interface BeltSt { b: GameBody; w: number; a: number; el: DevEl; s: number }
interface LiftSt { b: GameBody; w: number; a: number; el: DevEl; py: number; go?: boolean }
interface FanSt { el: DevEl; spin: number }
interface HiccupSt { el: DevEl; t0: number; hopT: number; n: number }

const objs = {
  // Fixed cable: pts = [[x,y],...]; the ends are plug points
  robot_cable: defineObj<CableSt>({
    layer: 'back',
    create(api, o) { const pts = opt(o).pts!; const el: CabEl = { k: 'cab', terms: [pts[0], pts[pts.length - 1]], pts }; RB(api).els.push(el); return { el }; },
    draw(ctx, st, t) {
      const o = opt(st.o);
      cable(ctx, o.pts!, st.el.live, t, o.col);
      // free ends = sockets (blink while unconnected; quiet from level 9 on)
      const u = st.el.used || [], f = st.el.fix || [];
      st.el.terms.forEach((p, i) => { if (!f[i]) socket(ctx, p[0], p[1], u[i], t, o.quiet); });
    },
  }),
  robot_batt: defineObj<BattSt>({
    layer: 'back',
    create(api, o) { const el: BattEl = { k: 'batt', terms: terms(o), x: o.x, y: o.y }; RB(api).els.push(el); return { el }; },
    draw(ctx, st) { drawBattery(ctx, st.o.x, st.o.y, 0, 1.15, false, st.el.on); },
  }),
  // Empty holder: only a drawing; the player's battery makes the circuit (terms() knows its plug points)
  robot_holder: defineObj({
    layer: 'back',
    create() { return {}; },
    draw(ctx, st, t, g) { holder(ctx, g, st.o.x, st.o.y); },
    blockers() { return []; },
  }),
  robot_lamp: defineObj<DevSt>({
    layer: 'back',
    create(api, o) { return { el: regDev(api, o) }; },
    draw(ctx, st, t, g) { lamp(ctx, g, st.o.x, st.o.y, st.el.powered, t); },
    blockers(o) { return [{ x: o.x, y: o.y - 8, r: 24 }]; },
  }),
  // Knife switch: the ball presses the blade down -> the gap closes (hold: only while something lies on it)
  robot_switch: defineObj<SwitchSt>({
    layer: 'back',
    intro(ctx, t, g, h) {
      const bx = Math.min(400, 40 + t * 2.6), on = bx > 210;
      g.block(ctx, 230, 232, 420, 18, 0, '#DCE4EF', '#8E9BB3', 8);
      cable(ctx, [[192, 219], [150, 219], [150, 90], [180, 90]], on, t); cable(ctx, [[268, 219], [320, 219], [320, 90], [290, 90]], on, t);
      lamp(ctx, g, 235, 70, on, t);
      knife(ctx, g, 230, 223, on ? 0 : 1);
      g.ball(ctx, h.fakeBall(bx, 207, 3, 0, 15), 'hero', t);
    },
    create(api, o) { const r = opt(o); const el: SwEl = { k: 'sw', terms: terms(r), closed: !!r.closed }; RB(api).els.push(el); return { el, down: r.closed ? 1 : 0 }; },
    step(api, st) {
      const o = opt(st.o), inz = bodiesIn(api, o.x - 34, o.x + 34, o.y - 46, o.y + 6).length > 0;
      const c = o.hold ? inz : (st.el.closed || inz);
      if (c !== st.el.closed) { st.el.closed = c; RB(api).t = -1; if (c) api.sfx('robot_click', o.x, o.y - 10, '#FFE36B', 12); }
      st.down += ((st.el.closed ? 1 : 0) - st.down) * 0.4;
    },
    draw(ctx, st, t, g) { knife(ctx, g, st.o.x, st.o.y, 1 - st.down, st.el.live); },
  }),
  // Gate hanging from a motor; pulled up when it has power
  robot_gate: defineObj<GateSt>({
    layer: 'back',
    create(api, ob) {
      const o = opt(ob), h = o.h || 110, w = 24;
      const b = api.add(api.R(o.x, o.y, w, h, { isStatic: true, friction: 0, restitution: 0.1, chamfer: { radius: 4 } })); b.kind = 'robot_gate';
      const hy = o.y - h / 2 - 22;
      const hb = api.add(api.R(o.x, hy, 84, 40, { isStatic: true, chamfer: { radius: 8 } })); hb.kind = 'robot_house';
      return { b, el: regDev(api, o, { y: hy }), off: 0, h, hy };
    },
    step(api, st) {
      ensure(api);
      const tgt = st.el.powered ? st.h + 4 : 0;
      if (st.off !== tgt) { st.off += Math.sign(tgt - st.off) * Math.min(5, Math.abs(tgt - st.off)); api.Body.setPosition(st.b, { x: st.o.x, y: st.o.y - st.off }); }
      st.b.isSensor = st.off > st.h - 12;
    },
    busy(api, st) { return st.off !== (st.el.powered ? st.h + 4 : 0); },
    blockers(ob) { const o = opt(ob), h = o.h || 110, hy = o.y - h / 2 - 22; return [{ x: o.x, y: hy, r: 34 }, { x: o.x, y: o.y - h / 4, r: 22 }, { x: o.x, y: o.y + h / 4, r: 22 }]; },
    draw(ctx, st, t, g) {
      const o = st.o, h = st.h, top = st.hy + 20;
      ctx.save(); ctx.beginPath(); ctx.rect(o.x - 30, top, 60, h + 20); ctx.clip();
      const gy = o.y - st.off;
      g.rr(ctx, o.x - 12, gy - h / 2, 24, h, 5); ctx.fillStyle = '#FFD84A'; ctx.fill();
      ctx.save(); ctx.clip(); ctx.fillStyle = INK; for (let k = -h; k < h; k += 26) { ctx.beginPath(); ctx.moveTo(o.x - 14, gy + k); ctx.lineTo(o.x + 14, gy + k - 18); ctx.lineTo(o.x + 14, gy + k - 6); ctx.lineTo(o.x - 14, gy + k + 12); ctx.fill(); } ctx.restore();
      g.rr(ctx, o.x - 12, gy - h / 2, 24, h, 5); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
      ctx.restore();
      motorBox(ctx, g, o.x, st.hy, st.el.powered, t);
      g.rr(ctx, o.x - 42, st.hy - 20, 84, 40, 9); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
      for (const p of st.el.terms) screw(ctx, p[0], p[1]);
    },
  }),
  // Conveyor belt: moves the ball in direction dir while it has power
  robot_belt: defineObj<BeltSt>({
    layer: 'back',
    create(api, ob) {
      const o = opt(ob), w = o.w || 300, a = (o.a || 0) * DEG;
      const b = api.add(api.R(o.x, o.y, w, 22, { isStatic: true, angle: a, friction: 0, restitution: 0.05, chamfer: { radius: 10 } })); b.kind = 'robot_belt';
      return { b, w, a, el: regDev(api, o), s: 0 };
    },
    step(api, st) {
      ensure(api);
      if (!st.el.powered) return;
      const o = opt(st.o), v = o.v || 4.5, d = o.dir || 1, tx = Math.cos(st.a) * d, ty = Math.sin(st.a) * d;
      st.s += v;
      for (const b of api.touching(st.b)) if (api.isDynamic(b)) {
        const vt = b.velocity.x * tx + b.velocity.y * ty, dv = (v - vt) * 0.35;
        api.Body.setVelocity(b, { x: b.velocity.x + tx * dv, y: b.velocity.y + ty * dv });
      }
    },
    blockers(ob) { const o = opt(ob), w = o.w || 300, a = (o.a || 0) * DEG, out = []; for (let k = -0.5; k <= 0.5; k += 0.25) out.push({ x: o.x + Math.cos(a) * w * k, y: o.y + Math.sin(a) * w * k, r: 20 }); return out; },
    draw(ctx, st, t, g) {
      const o = opt(st.o), w = st.w, on = st.el.powered;
      ctx.save(); ctx.lineCap = 'round';
      ctx.strokeStyle = INK; ctx.lineWidth = 8; ctx.beginPath(); ctx.moveTo(o.x, o.y + 6); ctx.lineTo(o.x, o.y + 40); ctx.stroke();
      ctx.translate(o.x, o.y); ctx.rotate(st.a);
      g.shadowOn(ctx, 10, 5); g.rr(ctx, -w / 2, -11, w, 22, 11); ctx.fillStyle = '#4A4560'; ctx.fill(); g.shadowOff(ctx);
      ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
      ctx.save(); g.rr(ctx, -w / 2, -11, w, 22, 11); ctx.clip();
      const off = ((st.s * (o.dir || 1)) % 24 + 24) % 24;
      ctx.strokeStyle = on ? '#FFC93C' : '#8D86A6'; ctx.lineWidth = 4;
      for (let x = -w / 2 - 24 + off; x < w / 2 + 24; x += 24) { ctx.beginPath(); ctx.moveTo(x, -11); ctx.lineTo(x - 6, -6); ctx.stroke(); }
      ctx.restore();
      for (const s of [-1, 1]) { ctx.save(); ctx.translate(s * (w / 2 - 11), 0); ctx.rotate(st.s / 9 * (o.dir || 1)); ctx.beginPath(); ctx.arc(0, 0, 8, 0, 7); ctx.fillStyle = '#C9D3E0'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke(); ctx.beginPath(); ctx.moveTo(-6, 0); ctx.lineTo(6, 0); ctx.stroke(); ctx.restore(); }
      if (on) { ctx.fillStyle = '#FFC93C'; ctx.strokeStyle = INK; ctx.lineWidth = 2.5; const d = o.dir || 1; for (const ax of [-w / 4, w / 4]) { ctx.beginPath(); ctx.moveTo(ax + d * 12, -22); ctx.lineTo(ax - d * 4, -31); ctx.lineTo(ax - d * 4, -13); ctx.closePath(); ctx.fill(); ctx.stroke(); } }
      ctx.restore();
      motorBox(ctx, g, o.x, o.y + 50, on, t);
      for (const p of st.el.terms) screw(ctx, p[0], p[1]);
    },
  }),
  // Scissor lift: the platform rises to 'top' when it has power and a ball rests on it
  robot_lift: defineObj<LiftSt>({
    layer: 'back',
    create(api, ob) {
      const o = opt(ob), w = o.w || 110, a = (o.a || 0) * DEG;
      const b = api.add(api.R(o.x, o.y, w, 16, { isStatic: true, angle: a, friction: 0, restitution: 0.02, chamfer: { radius: 6 } })); b.kind = 'robot_lift';
      return { b, w, a, el: regDev(api, Object.assign({}, o, { y0: o.y })), py: o.y };
    },
    step(api, st) {
      ensure(api);
      const o = opt(st.o);
      if (st.el.powered && !st.go && bodiesIn(api, o.x - st.w / 2, o.x + st.w / 2, st.py - 50, st.py).some(b => b.speed < 1.5)) st.go = true;
      const tgt = st.el.powered && st.go ? o.top! : o.y;
      if (st.py !== tgt) { st.py += Math.sign(tgt - st.py) * Math.min(o.v || 2.4, Math.abs(tgt - st.py)); api.Body.setPosition(st.b, { x: o.x, y: st.py }, true); }
      else api.Body.setVelocity(st.b, { x: 0, y: 0 });
    },
    busy(api, st) { return st.py !== (st.el.powered && st.go ? opt(st.o).top : st.o.y); },
    blockers(ob) { const o = opt(ob), out = []; for (let y = o.top!; y <= o.y + 30; y += 40) out.push({ x: o.x, y, r: (o.w || 110) / 2 - 6 }); return out; },
    draw(ctx, st, t, g) {
      const o = st.o, w = st.w, yb = o.y + 30, py = st.py, on = st.el.powered;
      ctx.save(); ctx.lineCap = 'round';
      const n = Math.max(1, Math.round((yb - py) / 50)), sh = (yb - py - 8) / n, hw = w / 2 - 14;
      for (let i = 0; i < n; i++) { const y0 = py + 8 + i * sh, y1 = y0 + sh; for (const c of ['#3B2A5C', '#C9D3E0']) { ctx.strokeStyle = c; ctx.lineWidth = c === INK ? 9 : 4; ctx.beginPath(); ctx.moveTo(o.x - hw, y0); ctx.lineTo(o.x + hw, y1); ctx.moveTo(o.x + hw, y0); ctx.lineTo(o.x - hw, y1); ctx.stroke(); } }
      ctx.restore();
      g.block(ctx, o.x, py, w, 16, st.a, on ? '#FFE07A' : '#FFD08A', on ? '#F2A516' : '#D9A050', 6);
      g.block(ctx, o.x, yb + 6, w + 16, 22, 0, '#BFD3E6', '#7F93AD', 8);
      ctx.beginPath(); ctx.arc(o.x, yb + 6, 5, 0, 7); ctx.fillStyle = on ? '#3FE07A' : '#6E6780'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.stroke();
      for (const p of st.el.terms) screw(ctx, p[0], p[1]);
    },
  }),
  // Fan: blows in direction a (degrees) while it has power
  robot_fan: defineObj<FanSt>({
    layer: 'back',
    create(api, o) { return { el: regDev(api, o), spin: 0 }; },
    step(api, st) {
      ensure(api);
      const o = opt(st.o); if (!st.el.powered) return;
      st.spin += 0.5;
      const a = (o.a || 0) * DEG, c = Math.cos(a), s = Math.sin(a), len = o.len || 300, hw = (o.w || 90) / 2;
      for (const b of api.S.dyn) {
        if (b.isStatic || b.kind === 'seesaw') continue;
        const dx = b.position.x - o.x, dy = b.position.y - o.y, u = dx * c + dy * s, v = -dx * s + dy * c;
        if (u > 20 && u < len && Math.abs(v) < hw) { const f = b.mass * 0.0011 * (o.power || 1.6) * (1 - u / len * 0.5); api.Body.applyForce(b, b.position, { x: c * f, y: s * f }); }
      }
    },
    blockers(o) { return [{ x: o.x, y: o.y, r: 44 }, { x: o.x, y: o.y + 44, r: 20 }]; },
    draw(ctx, st, t, g) {
      const o = opt(st.o), on = st.el.powered, a = (o.a || 0) * DEG;
      if (on) {
        ctx.save(); ctx.translate(o.x, o.y); ctx.rotate(a); ctx.strokeStyle = 'rgba(255,255,255,.85)'; ctx.lineWidth = 4; ctx.lineCap = 'round';
        const len = o.len || 300, hw = (o.w || 90) / 2;
        for (let i = 0; i < 5; i++) { const u = 50 + ((t * 6 + i * 67) % (len - 60)), v = (i - 2) * hw * 0.38; ctx.beginPath(); ctx.moveTo(u, v); ctx.quadraticCurveTo(u + 18, v - 6, u + 36, v); ctx.stroke(); }
        ctx.restore();
      }
      ctx.save(); ctx.strokeStyle = INK; ctx.lineWidth = 9; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(o.x, o.y + 20); ctx.lineTo(o.x, o.y + 50); ctx.stroke(); ctx.restore();
      box(ctx, g, o.x, o.y + 58, 124, 18, '#BFD3E6', '#7F93AD', 8);
      ctx.save(); ctx.translate(o.x, o.y); ctx.rotate(a);
      g.shadowOn(ctx, 10, 5); ctx.beginPath(); ctx.arc(0, 0, 40, 0, 7); ctx.fillStyle = on ? '#9EE4FF' : '#C9D8E8'; ctx.fill(); g.shadowOff(ctx); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
      ctx.save(); ctx.rotate(st.spin + (on ? t * 0.5 : 0));
      for (let i = 0; i < 3; i++) { ctx.rotate(Math.PI * 2 / 3); ctx.beginPath(); ctx.ellipse(18, 0, 17, 9, 0.3, 0, 7); ctx.fillStyle = '#FF8A70'; ctx.fill(); ctx.lineWidth = 2.5; ctx.stroke(); }
      ctx.restore();
      ctx.beginPath(); ctx.arc(0, 0, 7, 0, 7); ctx.fillStyle = '#FFC93C'; ctx.fill(); ctx.stroke();
      ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.lineWidth = 2; for (const r of [26, 36]) { ctx.beginPath(); ctx.arc(0, 0, r, -1.2, 1.2); ctx.stroke(); }
      ctx.restore();
      for (const p of st.el.terms) screw(ctx, p[0], p[1]);
    },
  }),
  // The hiccup robot: with power it hiccups – and its head pushes the ball up
  robot_hiccup: defineObj<HiccupSt>({
    layer: 'back',
    intro(ctx, t, g, h) {
      const on = t > 60, ph = on ? (t - 60) % 90 : 99, hop = ph < 14 ? Math.sin(ph / 14 * Math.PI) : 0;
      cable(ctx, [[152, 164], [90, 164], [90, 60], [170, 60]], on, t); cable(ctx, [[308, 164], [370, 164], [370, 60], [290, 60]], on, t);
      drawBattery(ctx, 230, 60, 0, 0.9, false, on);
      robotBody(ctx, g, 230, 260, on, hop, t);
      const by = ph < 30 ? 122 - Math.sin(Math.min(1, ph / 30) * Math.PI) * 90 : 122;
      g.ball(ctx, h.fakeBall(230, on ? by : 122, 0, 0, 15), 'hero', t);
    },
    create(api, o) {
      const x = o.x, y = o.y;
      const b = api.add(api.R(x, y - 55, 120, 110, { isStatic: true, friction: 0, restitution: 0.02, chamfer: { radius: 14 } })); b.kind = 'robot_hiccup_body';
      for (const s of [-1, 1]) { const l = api.add(api.R(x + s * 54, y - 116, 12, 14, { isStatic: true, chamfer: { radius: 5 } })); l.kind = 'robot_hiccup_lip'; }
      return { el: regDev(api, o), t0: -1, hopT: -99, n: 0 };
    },
    step(api, st) {
      ensure(api);
      const o = opt(st.o), S = api.S;
      if (!st.el.powered) { st.t0 = -1; return; }
      if (st.t0 < 0) st.t0 = S.t;
      const ev = o.every || 80, k = S.t - st.t0 - (o.first || 30);
      if (k >= 0 && k % ev === 0) {
        st.hopT = S.t; st.n++;
        api.sfx('robot_hic', o.x, o.y - 130, '#7FF3FF', 8);
        for (const b of bodiesIn(api, o.x - 62, o.x + 62, o.y - 175, o.y - 100)) { const p = o.pop || [0, -16]; api.Body.setVelocity(b, { x: p[0], y: p[1] }); }
      }
    },
    busy(api, st) { return st.el.powered && bodiesIn(api, st.o.x - 62, st.o.x + 62, st.o.y - 175, st.o.y - 100).length > 0; },
    blockers(o) { return [{ x: o.x, y: o.y - 55, r: 62 }]; },
    draw(ctx, st, t, g, S) {
      const ph = S ? S.t - st.hopT : 99, hop = ph >= 0 && ph < 14 ? Math.sin(ph / 14 * Math.PI) : 0;
      robotBody(ctx, g, st.o.x, st.o.y, st.el.powered, hop, t);
      for (const p of st.el.terms) screw(ctx, p[0], p[1]);
    },
  }),
};

// The whole level: solve the circuit once, then draw power flowing through the wires the child placed
function setup(api: PhysicsApi) {
  const rb = RB(api);
  solve(rb, api); rb.t = 0;
  api.thing({
    layer: 'mid',
    step(api) { ensure(api); },
    draw(ctx, t) {
      for (const el of rb.els) if (el.k === 'wire' && el.live) {
        const [a, b] = el.seg; ctx.save(); ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.strokeStyle = 'rgba(255,224,70,.35)'; ctx.lineWidth = 20; ctx.stroke();
        const mx = (a[0] + b[0]) / 2 - Math.sin(Math.atan2(b[1] - a[1], b[0] - a[0])) * -8, my = (a[1] + b[1]) / 2 + Math.cos(Math.atan2(b[1] - a[1], b[0] - a[0])) * 8;
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.quadraticCurveTo(mx, my, b[0], b[1]); flow(ctx, t); ctx.restore();
      }
      // short circuit: sparks at the battery pole
      for (const el of rb.els) if ((el.k === 'batt' || el.k === 'pbatt') && el.shorted && (t >> 2) % 3 === 0) {
        const p = el.k === 'batt' ? el.terms[1] : el.ends[1];
        ctx.save(); ctx.strokeStyle = '#FFE36B'; ctx.lineWidth = 3; ctx.beginPath();
        for (let i = 0; i < 6; i++) { const an = i + t * 0.7; ctx.moveTo(p[0], p[1]); ctx.lineTo(p[0] + Math.cos(an) * 16, p[1] + Math.sin(an) * 16); }
        ctx.stroke(); ctx.restore();
      }
    },
  });
}

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  robot_hum: (tone) => { tone(110, 0.35, 'sawtooth', 0.07, 220); tone(660, 0.15, 'sine', 0.12, 990, 0.05); },
  robot_click: (tone, noise) => { tone(2200, 0.03, 'square', 0.08); noise(0.06, 0.25, 4000, 2); tone(500, 0.08, 'triangle', 0.15, 300, 0.03); },
  robot_hic: (tone, noise) => { tone(300, 0.06, 'square', 0.12, 900); tone(900, 0.12, 'sine', 0.2, 1400, 0.05); noise(0.05, 0.15, 1500, 1); },
  robot_zap: (tone, noise) => { noise(0.18, 0.3, 6000, 1.5); tone(1800, 0.1, 'sawtooth', 0.05, 400); },
};

// ---------- reskins (the workshop's version of the core parts) ----------
const reskin: WorldDef['reskin'] = {
  plank(ctx, pl, sel, g, rs) { meccano(ctx, g, rs[0], sel); },
  long_plank(ctx, pl, sel, g, rs) { meccano(ctx, g, rs[0], sel); },
  pad(ctx, pl, sel, g, rs) { spring(ctx, g, rs[0], sel); },
  stone(ctx, pl, sel, g) { nut(ctx, g, pl.x, pl.y, (pl.a || 0) * DEG, sel); },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { meccano(ctx, g, { x: 0, y: 0, w: 100, h: 18, a: -0.35 }); },
  long_plank(ctx, g) { meccano(ctx, g, { x: 0, y: 0, w: 110, h: 16, a: -0.35 }); },
  pad(ctx, g) { spring(ctx, g, { x: 0, y: -8, w: 86, h: 16, a: 0 }); },
  stone(ctx, g) { nut(ctx, g, 0, 0, 0.2); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🤖', strings, music: 'robot.mp3',
  credits: { title: 'Playful Fizzy Technology', artist: 'REDproductions', id: 183156, url: 'https://pixabay.com/music/beats-playful-fizzy-technology-crispy-digital-comic-robotic-electronic-music-183156/' },
  synth: { bpm: 90, root: 246.9, sc: 'mixolydian', prog: [0, 6, 3, 4], bar: 4, lead: 'pizz', dens: 0.5 },
};

export default defineWorld({
  k: 'robot', meta, scene,
  wash: 'rgba(240,245,252,.6)',
  skins, ball, goal,
  parts, objs, setup, sounds,
  reskin, icons,
  levels,
});
